import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Header from '../components/landing/Header';
import FormsSection from '../components/landing/FormsSection';
import HowItWorksSection from '../components/landing/HowItWorksSection';
import TrustSection from '../components/landing/TrustSection';
import Footer from '../components/landing/Footer';
import MarketHero from '../components/landing/MarketHero';
import MarketSellerBuyerSplit from '../components/landing/MarketSellerBuyerSplit';
import SEOHead from '../components/SEOHead';
import { Toaster } from '../components/ui/sonner';
import { getCityBySlug, cities } from '../data/cities';

const buildLocalBusinessJsonLd = (city, canonical) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: `DreamHousesUSA — ${city.name}, ${city.stateAbbr}`,
  description: city.metaDescription,
  url: canonical,
  email: 'info@dreamhousesusa.com',
  areaServed: {
    '@type': 'City',
    name: city.name,
    containedInPlace: { '@type': 'State', name: city.state },
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: city.geo.lat,
    longitude: city.geo.lon,
  },
  knowsAbout: [
    `${city.name} real estate wholesaling`,
    `Cash home buyers ${city.name}`,
    `Off-market deals ${city.name}`,
    `${city.name} investment properties`,
  ],
});

const buildBreadcrumbJsonLd = (city, canonical) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.dreamhousesusa.com/' },
    { '@type': 'ListItem', position: 2, name: 'Markets', item: 'https://www.dreamhousesusa.com/#markets' },
    { '@type': 'ListItem', position: 3, name: `${city.name}, ${city.stateAbbr}`, item: canonical },
  ],
});

const NeighborhoodsAndZips = ({ city }) => (
  <section className="py-12 bg-slate-50">
    <div className="container mx-auto px-6 max-w-6xl">
      <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-6">
        Neighborhoods & ZIP codes we buy in {city.name}
      </h2>
      <div className="grid md:grid-cols-2 gap-8">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Neighborhoods</h3>
          <div className="flex flex-wrap gap-2">
            {city.neighborhoods.map((n) => (
              <span key={n} className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm text-slate-700">
                {n}
              </span>
            ))}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">ZIP Codes</h3>
          <div className="flex flex-wrap gap-2">
            {city.zips.map((z) => (
              <span key={z} className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-sm text-slate-700 font-mono">
                {z}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const RelatedCitiesRow = ({ relatedCities }) => (
  <section className="py-12 bg-white border-t border-slate-100">
    <div className="container mx-auto px-6 max-w-6xl">
      <h2 className="text-xl font-bold text-slate-900 mb-4">Other markets we serve</h2>
      <div className="flex flex-wrap gap-2">
        {relatedCities.map((c) => (
          <Link
            key={c.slug}
            to={`/we-buy-houses/${c.slug}`}
            data-testid={`related-city-${c.slug}`}
            className="px-3 py-1.5 bg-slate-100 hover:bg-emerald-600 hover:text-white rounded-full text-sm text-slate-700 transition-colors"
          >
            {c.name}, {c.stateAbbr}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const CityPage = () => {
  const { slug } = useParams();
  const city = getCityBySlug(slug);

  const relatedCities = useMemo(
    () => (city ? cities.filter((c) => c.slug !== city.slug).slice(0, 12) : []),
    [city]
  );

  if (!city) {
    return <Navigate to="/" replace />;
  }

  const canonical = `https://www.dreamhousesusa.com/we-buy-houses/${city.slug}`;
  const jsonLd = buildLocalBusinessJsonLd(city, canonical);
  const breadcrumb = buildBreadcrumbJsonLd(city, canonical);

  return (
    <div className="min-h-screen" data-testid={`city-page-${city.slug}`}>
      <Toaster position="top-right" richColors />
      <SEOHead
        title={city.metaTitle}
        description={city.metaDescription}
        keywords={city.keywords}
        canonical={canonical}
        jsonLd={jsonLd}
        jsonLdId="city-localbusiness-jsonld"
      />
      <SEOHead jsonLd={breadcrumb} jsonLdId="city-breadcrumb-jsonld" />
      <Header />
      <main className="pt-16">
        <MarketHero
          badge={`${city.name}, ${city.stateAbbr}`}
          headline={city.headline}
          intro={city.intro}
          ctaLabel={city.name}
        />
        <MarketSellerBuyerSplit
          marketName={city.name}
          sellerCopy={city.sellerCopy}
          buyerCopy={city.buyerCopy}
        />
        <NeighborhoodsAndZips city={city} />
        <HowItWorksSection />
        <FormsSection
          source={`city:${city.slug}`}
          sourceCityState={`${city.name}, ${city.stateAbbr}`}
          sourceMarket={`${city.name}, ${city.stateAbbr}`}
        />
        <TrustSection />
        <RelatedCitiesRow relatedCities={relatedCities} />
      </main>
      <Footer />
    </div>
  );
};

export default CityPage;
